import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { formatCurrency } from '../../utils/currencyUtils';

const ListContainer = styled.div`
  margin-top: 10px;
`;

const BudgetItem = styled.div`
  background: linear-gradient(145deg, #ffffff, #f5f6fa);
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 16px;
  box-shadow: 
    4px 4px 10px rgba(0, 0, 0, 0.05),
    -4px -4px 10px rgba(255, 255, 255, 0.8);
`;

const CategoryName = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #4a6fa1;
  margin-bottom: 12px;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 8px;
  background: #e6ebf5;
  border-radius: 4px;
  overflow: hidden;
  box-shadow: inset 1px 1px 3px rgba(0, 0, 0, 0.1),
              inset -1px -1px 3px rgba(255, 255, 255, 0.7);
  margin-bottom: 10px;
`;

const Progress = styled.div`
  height: 100%;
  background-color: ${props => props.$color};
  width: ${props => props.$percentage}%;
  transition: width 0.3s ease;
`;

const BudgetInfo = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 14px;
  color: #8c96a5;
  margin-bottom: 14px;
`;

// Добавляем стиль для информации о создателе
const CreatorInfo = styled.div`
  font-size: 12px;
  color: #8c96a5;
  margin-bottom: 10px;
  font-style: italic;
`;

const ButtonsContainer = styled.div`
  display: flex;
  gap: 8px;
`;

const EditButton = styled.button`
  flex: 1;
  background: linear-gradient(145deg, #f0f3f9, #e6ebf5);
  color: #4a6fa1;
  border: none;
  padding: 8px 0;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 2px 2px 5px rgba(0, 0, 0, 0.05),
              -2px -2px 5px rgba(255, 255, 255, 0.8);
  
  &:hover {
    background: linear-gradient(145deg, #e6ebf5, #dce3f0);
    transform: translateY(-2px);
  }
  
  &:active {
    transform: translateY(0);
    box-shadow: inset 1px 1px 3px rgba(0, 0, 0, 0.1),
                inset -1px -1px 3px rgba(255, 255, 255, 0.7);
  }
`;

const DeleteButton = styled(EditButton)`
  background: linear-gradient(145deg, #ff7e7e, #ff6b6b);
  color: white;
  
  &:hover {
    background: linear-gradient(145deg, #ff6b6b, #ff5c5c);
  }
`;

const LoadingContainer = styled.div`
  text-align: center;
  padding: 20px 0;
  color: #8c96a5;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 30px 0;
  background: linear-gradient(145deg, #ffffff, #f5f6fa);
  border-radius: 12px;
  box-shadow: 
    4px 4px 10px rgba(0, 0, 0, 0.05),
    -4px -4px 10px rgba(255, 255, 255, 0.8);
  color: #8c96a5;
  font-size: 16px;
`;

const BudgetList = ({ 
  budgets, 
  isLoading, 
  exchangeRates,
  defaultCurrency,
  onEdit, 
  onDelete,
  showSharedInfo = false  // Добавляем пропс для показа информации о создателе
}) => {
  const { t } = useTranslation();

  // Функция для конвертации суммы между валютами
  const convertCurrency = (amount, fromCurrency, toCurrency) => {
    if (fromCurrency === toCurrency) return amount;
    
    const rate = exchangeRates[`${fromCurrency}_${toCurrency}`];
    return rate ? amount * rate : amount;
  };

  if (isLoading) {
    return <LoadingContainer>{t('loading')}</LoadingContainer>;
  }

  if (!budgets || budgets.length === 0) {
    return <EmptyState>{t('budget_not_found')}</EmptyState>;
  }

  return (
    <ListContainer>
      {budgets.map((budget, index) => {
        // Расчет процента использования бюджета
        let totalSpent = parseFloat(budget.total_spent) || 0;
        let limitAmount = parseFloat(budget.limit_amount) || 1;
        let budgetCurrency = budget.currency || defaultCurrency;
        
        // Конвертация расходов в валюту бюджета, если необходимо
        if (budget.spent_currency && budget.spent_currency !== budgetCurrency) {
          totalSpent = convertCurrency(
            totalSpent, 
            budget.spent_currency, 
            budgetCurrency
          );
        }
        
        // Расчет процента и определение цвета индикатора
        let percentage = (totalSpent / limitAmount) * 100;
        percentage = Math.min(percentage, 100); // Ограничиваем до 100%
        
        let progressColor = percentage >= 100 
          ? "#EB5757" 
          : percentage >= 80 
            ? "#F2994A" 
            : "#2F80ED";

        return (
          <BudgetItem key={`budget-${index}`}>
            <CategoryName>{budget.category}</CategoryName>
            
            {/* Добавляем информацию о создателе, если доступна */}
            {showSharedInfo && budget.owner_name && (
              <CreatorInfo>
                {t('created_by')}: {budget.owner_name}
                {budget.is_shared && ` (${t('shared')})`}
              </CreatorInfo>
            )}
            
            <ProgressBar>
              <Progress $percentage={percentage} $color={progressColor} />
            </ProgressBar>
            <BudgetInfo>
              <span>
                {t('spent')}: {formatCurrency(totalSpent, budgetCurrency)}
              </span>
              <span>
                {t('limit_amount')}: {formatCurrency(limitAmount, budgetCurrency)}
              </span>
            </BudgetInfo>
            <ButtonsContainer>
              <EditButton onClick={() => onEdit(budget.category, limitAmount, budgetCurrency)}>
                {t('edit_budget')}
              </EditButton>
              <DeleteButton onClick={() => onDelete(budget.category)}>
                {t('delete_budget')}
              </DeleteButton>
            </ButtonsContainer>
          </BudgetItem>
        );
      })}
    </ListContainer>
  );
};

export default BudgetList;